import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { WidgetService } from './widget.service';

/**
 * Represents an open editor or file viewer on the desktop
 */
export interface OpenEditor {
  widgetId: string;
  widgetType: string;
  path: string;
  cursorLine?: number;
  cursorColumn?: number;
  isDirty?: boolean;
}

/**
 * Represents the current desktop state for context injection
 */
export interface DesktopState {
  editors: OpenEditor[];
  activeEditorPath?: string;
}

/**
 * Service to track the state of the desktop including open editors and files.
 * This state is injected into chat messages so AI agents know what files
 * the user is working on.
 */
@Injectable({
  providedIn: 'root'
})
export class DesktopStateService {
  private state: DesktopState = { editors: [] };
  private stateSubject = new BehaviorSubject<DesktopState>(this.state);

  public state$ = this.stateSubject.asObservable();

  constructor() {}

  /**
   * Register an editor widget with a file path
   */
  registerEditor(widgetId: string, widgetType: string, path: string): void {
    // Remove existing entry if present (widget reopened with different file)
    this.state.editors = this.state.editors.filter(e => e.widgetId !== widgetId);

    this.state.editors.push({
      widgetId,
      widgetType,
      path,
      isDirty: false
    });

    this.emit();
  }

  /**
   * Unregister an editor widget when closed
   */
  unregisterEditor(widgetId: string): void {
    this.state.editors = this.state.editors.filter(e => e.widgetId !== widgetId);

    // Clear active if it was the unregistered one
    const wasActive = this.state.editors.find(e => e.path === this.state.activeEditorPath);
    if (!wasActive) {
      this.state.activeEditorPath = undefined;
    }

    this.emit();
  }

  /**
   * Update cursor position for an editor
   */
  updateCursor(widgetId: string, line: number, column: number): void {
    const editor = this.state.editors.find(e => e.widgetId === widgetId);
    if (editor) {
      editor.cursorLine = line;
      editor.cursorColumn = column;
      this.emit();
    }
  }

  /**
   * Update dirty state for an editor
   */
  updateDirtyState(widgetId: string, isDirty: boolean): void {
    const editor = this.state.editors.find(e => e.widgetId === widgetId);
    if (editor) {
      editor.isDirty = isDirty;
      this.emit();
    }
  }

  /**
   * Set the active/focused editor by widget ID
   */
  setActiveEditor(widgetId: string): void {
    const editor = this.state.editors.find(e => e.widgetId === widgetId);
    if (editor) {
      this.state.activeEditorPath = editor.path;
      this.emit();
    }
  }

  /**
   * Get current desktop state
   */
  getState(): DesktopState {
    return { ...this.state };
  }

  /**
   * Format desktop state as XML for injection into chat messages
   * @deprecated Use toJSON() instead — being replaced by unified context envelope
   */
  formatAsXml(): string {
    if (this.state.editors.length === 0) {
      return '';
    }

    const lines: string[] = ['<desktop-state>'];

    for (const editor of this.state.editors) {
      const attrs: string[] = [`path="${editor.path}"`];

      if (editor.cursorLine !== undefined) {
        attrs.push(`cursor-line="${editor.cursorLine}"`);
      }
      if (editor.cursorColumn !== undefined) {
        attrs.push(`cursor-column="${editor.cursorColumn}"`);
      }
      if (editor.isDirty) {
        attrs.push('dirty="true"');
      }
      if (editor.path === this.state.activeEditorPath) {
        attrs.push('active="true"');
      }

      lines.push(`  <editor ${attrs.join(' ')} />`);
    }

    lines.push('</desktop-state>');
    return lines.join('\n');
  }

  /**
   * Return desktop state as a plain object for the unified context envelope.
   */
  toJSON(): any {
    if (this.state.editors.length === 0) return null;

    return {
      editors: this.state.editors.map(e => {
        const obj: any = { path: e.path };
        if (e.path === this.state.activeEditorPath) obj.active = true;
        if (e.cursorLine !== undefined) obj.cursor_line = e.cursorLine;
        if (e.isDirty) obj.dirty = true;
        return obj;
      })
    };
  }

  private emit(): void {
    this.stateSubject.next({ ...this.state });
  }
}
